'use client';

import { AnimateOnScroll } from '@/components/animate-on-scroll';
import { cn } from '@/lib/utils';
import './evidence-triptych.css';

// Every visible string below already exists verbatim elsewhere on the site
// (hero mock, proof band, /audit-transparency) — no new claims (plan ruling).
//
// Motion (DESIGN.md "Signature: Hairline Draws"): one reveal when the block
// enters view. The hairline draws left to right over --motion-draw and each
// card lights from 45% to full as the line reaches it. `--light-delay` is the
// moment the draw's ease-out reaches that card's left edge (250ms lead-in +
// 0 / 10% / 22% of 900ms). No scroll-linked runway: the block is the height of
// its cards on every browser.
const NODES = [
  {
    eyebrow: 'HCC Capture',
    body: (
      <>
        <div className="text-sm font-semibold leading-snug">
          Type 2 diabetes with CKD
          <br />
          <span className="font-code text-primary">E11.22</span>
        </div>
      </>
    ),
    lightDelay: '250ms',
  },
  {
    eyebrow: 'Evidence Linked',
    body: (
      <>
        <div className="text-sm leading-relaxed text-muted-foreground">
          <span className="triptych-evidence-mark">CKD stage 3</span>
        </div>
        <div className="mt-2 text-[11px] font-medium text-primary">↳ Nephrology consult · 04/2023</div>
      </>
    ),
    lightDelay: '340ms',
  },
  {
    eyebrow: 'Claim Submitted',
    body: (
      <>
        <div className="flex items-center gap-2 text-sm font-semibold">
          <span className="font-code text-primary">E11.22</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-verified-wash px-2 py-0.5 text-[10px] font-bold text-verified">
            RAF 1.34
          </span>
        </div>
        <div className="mt-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">MEAT</div>
      </>
    ),
    lightDelay: '450ms',
  },
];

export default function EvidenceTriptych({ className }: { className?: string }) {
  return (
    <AnimateOnScroll animationClassName="animate-fade-in" className={cn('triptych-grid', className)} aria-hidden="true">
      <div className="triptych-thread" />
      {NODES.map((n) => (
        <div key={n.eyebrow} className="triptych-node" style={{ ['--light-delay' as string]: n.lightDelay }}>
          <div className="text-[10px] font-bold uppercase tracking-widest text-primary">{n.eyebrow}</div>
          <div className="mt-2">{n.body}</div>
        </div>
      ))}
    </AnimateOnScroll>
  );
}
